/**
 * O escopo de cliente e lead, e os caminhos de arquivo: as regras puras que o legado aplica antes
 * de cada leitura, gravação e upload da ficha da cliente.
 *
 * NO LEGADO elas leem duas variáveis globais (`clienteSelecionado` e `leadAdminSelecionado`). Aqui
 * o escopo é um valor passado a cada chamada, e isso não muda o resultado: muda só que a regra
 * pode ser testada sem uma tela aberta.
 */

/** A cliente aberta e o lead em contexto. `leadId` nulo é a cliente sem lead (caso raro, 0 leads). */
export interface Escopo {
  // O `user_id` da cliente, que é a chave do RLS em todas as tabelas da ficha.
  readonly userId: string;
  // O `id` do lead aberto, ou `null` quando a cliente não tem lead em contexto.
  readonly leadId: number | null;
}

/** O mínimo de uma consulta que aceita filtro de igualdade encadeado, como a do PostgREST. */
export interface ConsultaComFiltro<Q> {
  eq(coluna: string, valor: string | number): Q;
}

/**
 * Aplica o escopo a uma consulta, como `comEscopo` de `admin.html:3320`.
 *
 * SEM LEAD, NÃO HÁ FILTRO DE LEAD, e não um filtro `lead_id is null`: é o que o legado faz, e o
 * comentário dele explica o porquê ("Cliente sem lead em contexto continua funcionando sem o
 * filtro"). Trocar por `is null` esconderia as linhas antigas, gravadas antes de existir lead.
 */
export function comEscopo<Q extends ConsultaComFiltro<Q>>(consulta: Q, escopo: Escopo): Q {
  // A cliente sempre filtra.
  const daCliente = consulta.eq('user_id', escopo.userId);
  // O lead só filtra quando existe, como o `leadAdminSelecionado ? ... : query` do legado.
  return escopo.leadId !== null ? daCliente.eq('lead_id', escopo.leadId) : daCliente;
}

/**
 * Monta o registro de uma gravação já com `user_id` e `lead_id`, como `registroComLead` de
 * `admin.html:3326`.
 *
 * A ORDEM é a do legado: os dois campos do escopo primeiro, e os campos da tela por cima. Um campo
 * da tela com o mesmo nome vence, exatamente como o espalhamento do original.
 */
export function registroComLead<T extends object>(
  escopo: Escopo,
  campos: T,
): { user_id: string; lead_id: number | null } & T {
  return { user_id: escopo.userId, lead_id: escopo.leadId, ...campos };
}

/**
 * A pasta de arquivos da cliente no bucket `materiais`, como `caminhoArquivosCliente` de
 * `admin.html:6399`: por lead quando há lead aberto, e a pasta achatada antiga quando não há.
 */
export function caminhoArquivosCliente(escopo: Escopo, subpasta: string): string {
  // Com lead: `<user_id>/<lead_id>/<subpasta>`.
  if (escopo.leadId !== null) return `${escopo.userId}/${escopo.leadId}/${subpasta}`;
  // Sem lead: `<user_id>/<subpasta>`, o formato de antes de existir lead.
  return `${escopo.userId}/${subpasta}`;
}

/**
 * A pasta dos arquivos administrativos, fora de qualquer cliente (`admin.html:6418`). Só o admin
 * tem policy de Storage para ela.
 */
export const CAMINHO_ARQUIVOS_ADMIN = 'admin-arquivos';

/**
 * O nome de arquivo aceito pelo Storage, como `sanitizarNomeArquivo` de `admin.html:7628`: tira os
 * acentos e troca por `_` tudo que não for letra, número, `_`, `-`, `.`, parênteses ou espaço.
 */
export function sanitizarNomeArquivo(nome: string): string {
  return (
    nome
      // Separa a letra do acento (NFD) para o passo seguinte remover só o acento.
      .normalize('NFD')
      // Remove os acentos combinantes (U+0300 a U+036F), que ficaram soltos depois do NFD.
      .replace(/[̀-ͯ]/g, '')
      // O resto que o Storage recusaria vira `_`, com a mesma lista de permitidos do legado.
      .replace(/[^\w\-.() ]/g, '_')
  );
}
